// Speaker app entry point.
//
// 1. Show boot screen unless URL has both ?deck=<deck-id>&session=<id>.
// 2. Load /config.json (CDK-generated) — contains AWS resource IDs.
// 3. Spin up IotClient (unauth — no admin login required to control a session).
// 4. Mount the module registry; each module renders into its slot.
// 5. The slide-preview module loads the deck in an iframe at role=speaker, which
//    causes that iframe's sync-plugin to publish slide events to MQTT. The
//    actual viewer (projector) URL receives those and follows.

import { loadConfig }     from '../shared/config.js';
import { EventBus }       from '../shared/event-bus.js';
import { IotClient }      from '../shared/iot-client.js';
import { initPool, existingSession, redirectToLogin } from '../shared/cognito-session.js';
import { ModuleRegistry } from './core/module-registry.js';
import { DeckBridge }     from './core/deck-bridge.js';
import { loadManifest }   from './core/manifest.js';
import { initResizers }   from './core/resizers.js';

import slidePreview     from './modules/slide-preview.js';
import nextSlide        from './modules/next-slide.js';
import notes            from './modules/notes.js';
import paceTracker      from './modules/pace-tracker.js';
import navControls      from './modules/nav-controls.js';
import mediaControls    from './modules/media-controls.js';
import connectionStatus from './modules/connection-status.js';
import toc              from './modules/toc.js';
import slideCounter     from './modules/slide-counter.js';

const shell = document.getElementById('speaker-shell');

function setState(s) { shell.dataset.state = s; }

function readUrl() {
  const p = new URLSearchParams(location.search);
  return { deck: p.get('deck'), session: p.get('session') };
}

function setHeader({ deckId, session }) {
  document.querySelector('[data-slot="deck-title"]').textContent = deckId || '';
  document.querySelector('[data-slot="session"]').textContent    = session ? `session ${session}` : '';
}

async function showBoot() {
  // Boot screen still requires login (manifest is fetched via Cognito-signed S3 reads).
  const config = await loadConfig();
  initPool(config);
  const auth = await existingSession();
  if (!auth) { redirectToLogin(); return; }

  setState('boot');
  const select = document.querySelector('select[name="deck"]');
  try {
    const { decks = [] } = await loadManifest();
    select.innerHTML = decks.length
      ? decks.map(d => `<option value="${d.id}">${d.title || d.id}</option>`).join('')
      : '<option disabled>(no decks uploaded yet)</option>';
  } catch {
    select.innerHTML = '<option disabled>(manifest unavailable)</option>';
  }
  document.getElementById('boot-form').addEventListener('submit', (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const deck    = fd.get('deck');
    const session = fd.get('session');
    location.search = `?deck=${encodeURIComponent(deck)}&session=${encodeURIComponent(session)}`;
  });
}

async function start({ deckId, session }) {
  setHeader({ deckId, session });
  setState('loading');

  const config = await loadConfig();
  initPool(config);
  const auth = await existingSession();
  if (!auth) { redirectToLogin(); return; }

  const bus    = new EventBus();
  const iot    = new IotClient({ config, session, from: 'speaker', bus, idToken: auth.idToken });
  const deck   = new DeckBridge();
  const reg    = new ModuleRegistry();

  // Register modules. To add a new feature: drop a file in modules/ and add it here.
  reg.register(slidePreview);
  reg.register(nextSlide);
  reg.register(toc);
  reg.register(notes);
  reg.register(paceTracker);
  reg.register(navControls);
  reg.register(mediaControls);
  reg.register(slideCounter);
  reg.register(connectionStatus);

  await reg.mountAll({ session, deckId, config, iot, bus, deck, shell });

  // Pre-warm: open the IoT connection now, before any keypress.
  await iot.connect();

  setState('running');
  initResizers();

  window.addEventListener('beforeunload', () => iot.disconnect());
}

(async () => {
  const { deck, session } = readUrl();
  if (!deck || !session) { await showBoot(); return; }
  try { await start({ deckId: deck, session }); }
  catch (e) {
    console.error('[speaker-app] startup failed', e);
    setState('boot');
    alert('Startup failed: ' + e.message);
  }
})();
